import {
  ArcgisOptions,
  FeatureService,
  GeoJsonSourceOptions,
} from "@areahub/arcgis";
import { useBoolean } from "@chakra-ui/react";
import { MutableRefObject } from "react";
import { FeatureServiceLayerDefinition } from "./use-feature-service";

export interface UseLoadableServicesOptions {
  url: string;
  layer: FeatureServiceLayerDefinition;
  hasHighlightLayer?: boolean;
  arcgisOptions?: Omit<ArcgisOptions, "url">;
  geojsonSourceOptions?: GeoJsonSourceOptions;
  [x: string]: any;
}

export interface UseLoadableFeatureServiceReturn {
  load: () => void;
  toggleVisibility: () => void;
  highlightLayer: any;
  hidden: boolean;
  layer: FeatureServiceLayerDefinition;
}

/**
 * !Keep this for use on the wildfires app once migrated in the future
 */
export const useLoadableFeatureService = (
  ref: MutableRefObject<any>,
  {
    url,
    layer,
    arcgisOptions,
    hasHighlightLayer,
    geojsonSourceOptions,
    ...rest
  }: UseLoadableServicesOptions,
): UseLoadableFeatureServiceReturn => {
  const [hidden, hideLayer] = useBoolean(true);
  const [loaded, setLoadStatus] = useBoolean(false);
  const highlightLayer = {
    ...layer,
    beforeId: layer.id,
    id: `${layer.id}-highlight`,
    ...rest?.highlightLayer,
  };
  const load = () => {
    const map = ref.current?.getMap();
    const fs = new FeatureService(
      layer.source,
      ref?.current?.getMap(),
      {
        url: url,
        ...arcgisOptions,
      },
      geojsonSourceOptions,
    );
    if (hasHighlightLayer) {
      map.addLayer(highlightLayer);
    }
    hideLayer.off();
    setLoadStatus.on();
    map.addLayer(layer);
  };

  const toggleVisibility = () => {
    if (ref?.current) {
      if (!loaded) {
        load();
      } else {
        ref?.current
          .getMap()
          .setLayoutProperty(
            layer.id,
            "visibility",
            !hidden ? "none" : "visible",
          );
        hideLayer.toggle();
      }
    }
  };
  return {
    load,
    toggleVisibility,
    highlightLayer,
    hidden,
    layer,
  };
};
